import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Collapse,
  alpha,
  useTheme,
  useMediaQuery,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  DeleteSweep as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as CopyIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  Terminal as TerminalIcon,
  ArrowBack as BackIcon,
  OpenInFull as OpenFullIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { proxyService, ProxyLogEntry } from '../services/proxyService';
import { SPACING, BORDER_RADIUS, ELEVATION } from '../styles/testCycleDesignTokens';

const METHOD_COLORS: Record<string, string> = {
  GET: '#61affe',
  POST: '#49cc90',
  PUT: '#fca130',
  DELETE: '#f93e3e',
  PATCH: '#50e3c2',
};

/** AppBar height — full-view overlay must start below it so the back control stays visible. */
const APP_HEADER_HEIGHT_PX = 64;

/** Max response body size for display in console (5 MB). Larger bodies show an overflow message. */
const MAX_RESPONSE_BODY_BYTES = 5 * 1024 * 1024;

/** Inline console preview: only the first 10 KB; use full view for the complete payload. */
const RESPONSE_BODY_PREVIEW_MAX_BYTES = 10 * 1024;

function getResponseBodyByteSize(body: unknown): number {
  if (body === undefined || body === null) return 0;
  const str = typeof body === 'string' ? body : JSON.stringify(body);
  return new TextEncoder().encode(str).length;
}

function formatBytesToMB(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(2);
}

function formatByteSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${formatBytesToMB(bytes)} MB`;
}

/** Decode percent-encoding for Console display so query values match the request editor. */
function formatUrlForConsoleDisplay(url: string): string {
  if (!url) return url;
  try {
    const parsed = new URL(url);
    const decodedSearch = parsed.search
      ? `?${parsed.search
          .slice(1)
          .split('&')
          .map((pair) => {
            const eq = pair.indexOf('=');
            if (eq < 0) {
              try {
                return decodeURIComponent(pair);
              } catch {
                return pair;
              }
            }
            const k = pair.slice(0, eq);
            const v = pair.slice(eq + 1);
            try {
              return `${decodeURIComponent(k)}=${decodeURIComponent(v)}`;
            } catch {
              return pair;
            }
          })
          .join('&')}`
      : '';
    let pathname = parsed.pathname;
    try {
      pathname = decodeURIComponent(pathname);
    } catch {
      // keep encoded pathname
    }
    return `${parsed.origin}${pathname}${decodedSearch}${parsed.hash}`;
  } catch {
    try {
      return decodeURIComponent(url);
    } catch {
      return url;
    }
  }
}

/** Truncate a string to at most `maxBytes` UTF-8 bytes (safe for multi-byte characters). */
function truncateStringToBytes(str: string, maxBytes: number): string {
  const encoder = new TextEncoder();
  if (encoder.encode(str).length <= maxBytes) return str;
  let low = 0;
  let high = str.length;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (encoder.encode(str.slice(0, mid)).length <= maxBytes) {
      low = mid;
    } else {
      high = mid - 1;
    }
  }
  return str.slice(0, low);
}

type FilterType = 'all' | 'errors' | 'requests';

type LogLevel = 'log' | 'info' | 'warning' | 'error';

interface ConsoleProps {
  embedded?: boolean;
  standalone?: boolean;
  /** When set with `embedded`, shows a header control to dismiss the split-view console. */
  onClose?: () => void;
}

const Console: React.FC<ConsoleProps> = ({ embedded = false, standalone = false, onClose }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const [entries, setEntries] = useState<ProxyLogEntry[]>([]);
  const [filter, setFilter] = useState<FilterType>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [copySnack, setCopySnack] = useState(false);
  const [levels, setLevels] = useState<Record<LogLevel, boolean>>({
    log: true,
    info: true,
    warning: true,
    error: true,
  });
  const [showTimestamp, setShowTimestamp] = useState(true);
  const [levelMenuAnchorEl, setLevelMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [responseBodyFullViewEntryId, setResponseBodyFullViewEntryId] = useState<string | null>(null);
  const [requestBodyFullViewEntryId, setRequestBodyFullViewEntryId] = useState<string | null>(null);
  const [responseBodyOverflowDialog, setResponseBodyOverflowDialog] = useState<{
    open: boolean;
    sizeBytes: number;
  }>({ open: false, sizeBytes: 0 });

  useEffect(() => {
    const unsubscribe = proxyService.subscribeToProxyLogs((next) => setEntries(next));
    return unsubscribe;
  }, []);

  const getLogLevel = (entry: ProxyLogEntry): LogLevel => {
    if (entry.error) return 'error';
    if (entry.status != null) {
      if (entry.status >= 500) return 'error';
      if (entry.status >= 400) return 'warning';
      if (entry.status >= 200) return 'info';
    }
    return 'log';
  };

  const filteredEntries = useMemo(() => {
    let base = entries;
    if (filter === 'errors') {
      base = base.filter((e) => e.error != null);
    } else if (filter === 'requests') {
      base = base.filter((e) => !e.error);
    }
    return base.filter((e) => levels[getLogLevel(e)]);
  }, [entries, filter, levels]);

  const handleClear = useCallback(() => {
    proxyService.clearProxyLogs();
    setExpandedId(null);
    setResponseBodyFullViewEntryId(null);
    setRequestBodyFullViewEntryId(null);
  }, []);

  const handleCopy = useCallback((text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySnack(true);
      window.setTimeout(() => setCopySnack(false), 2000);
    });
  }, []);

  const handleLevelMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLevelMenuAnchorEl(event.currentTarget);
  };

  const handleLevelMenuClose = () => {
    setLevelMenuAnchorEl(null);
  };

  const toggleLevel = (level: LogLevel) => {
    setLevels((prev) => ({
      ...prev,
      [level]: !prev[level],
    }));
  };

  const allLevelsSelected = Object.values(levels).every(Boolean);
  const someLevelsSelected = Object.values(levels).some(Boolean);

  const formatTime = (ts: number) => {
    const d = new Date(ts);
    const h = String(d.getHours()).padStart(2, '0');
    const m = String(d.getMinutes()).padStart(2, '0');
    const s = String(d.getSeconds()).padStart(2, '0');
    const ms = String(d.getMilliseconds()).padStart(3, '0');
    return `${h}:${m}:${s}.${ms}`;
  };

  const formatResponseHeaderValue = (headerName: string, headerValue: string): string => {
    // Convert HTTP Date header into user's local timezone for easier correlation with app timestamps.
    if (headerName.toLowerCase() !== 'date') {
      return headerValue;
    }
    const parsed = new Date(headerValue);
    if (Number.isNaN(parsed.getTime())) {
      return headerValue;
    }
    return parsed.toLocaleString([], {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  const formatRequestBodyForDisplay = (body: unknown): string => {
    if (body === undefined || body === null) return '';
    if (typeof body === 'string') {
      if (body === '[multipart/binary upload]' || body.startsWith('[multipart/binary upload')) {
        return `${body}\n\n(Resend the request to see detailed form-data/binary metadata.)`;
      }
      return body;
    }
    if (typeof body === 'object') {
      const record = body as Record<string, unknown>;
      if (record.type === 'form-data' && Array.isArray(record.fields)) {
        const lines: string[] = ['FORM DATA', ''];
        if (record.note) {
          lines.push(String(record.note));
          lines.push('');
        }
        for (const field of record.fields as Array<Record<string, unknown>>) {
          const key = String(field.key ?? '');
          if (field.fieldType === 'file' || field.type === 'file') {
            lines.push(`Field: ${key}`);
            lines.push(`File name: ${field.fileName ?? field.value ?? '(file)'}`);
            if (field.contentType) lines.push(`Content-Type: ${field.contentType}`);
            if (field.size ?? field.sizeBytes) lines.push(`Size: ${field.size ?? field.sizeBytes}`);
            if (field.lastModified) lines.push(`Last modified: ${field.lastModified}`);
            if (field.contentTooLarge && field.contentLimitNote) {
              lines.push('');
              lines.push(String(field.contentLimitNote));
            } else if (typeof field.fileContent === 'string') {
              lines.push('');
              if (field.parseError) {
                lines.push(String(field.parseError));
                lines.push('');
              }
              lines.push('FILE CONTENT');
              lines.push(String(field.fileContent));
            }
          } else {
            lines.push(`Field: ${key}`);
            lines.push(`Value: ${field.value ?? ''}`);
          }
          lines.push('');
        }
        return lines.join('\n').trimEnd();
      }
      if (record.type === 'binary') {
        const lines = [
          'Binary request body',
          `  file name: ${record.fileName ?? '(unknown)'}`,
          `  content-type: ${record.contentType ?? 'application/octet-stream'}`,
          `  size: ${record.size ?? record.sizeBytes ?? 'unknown'}`,
          record.lastModified ? `  last modified: ${record.lastModified}` : '',
        ].filter(Boolean) as string[];
        if (record.contentTooLarge && record.contentLimitNote) {
          lines.push('');
          lines.push(String(record.contentLimitNote));
        } else if (typeof record.fileContent === 'string') {
          lines.push('');
          if (record.parseError) {
            lines.push(String(record.parseError));
            lines.push('');
          }
          lines.push('FILE CONTENT');
          lines.push(String(record.fileContent));
        } else if (record.note) {
          lines.push('');
          lines.push(String(record.note));
        }
        return lines.join('\n');
      }
    }
    return typeof body === 'string' ? body : JSON.stringify(body, null, 2);
  };

  const bodyToString = (body: unknown): string => {
    if (body === undefined || body === null) return '';
    return formatRequestBodyForDisplay(body);
  };

  const renderBodyBlock = (
    body: unknown,
    label: string,
    options?: { maxHeight?: number; onExpand?: () => void; expandTitle?: string }
  ) => {
    if (body === undefined || body === null) return null;
    const str = bodyToString(body);
    const maxHeight = options?.maxHeight ?? 240;
    return (
      <Box sx={{ mt: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 0.5,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}
          >
            {label}
          </Typography>
          {options?.onExpand && (
            <Tooltip title={options.expandTitle ?? 'View Full Request Body'} describeChild>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  options.onExpand?.();
                }}
                aria-label={options.expandTitle ?? 'View Full Request Body'}
                sx={{
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <OpenFullIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            bgcolor: alpha(theme.palette.background.default, 0.6),
            fontFamily: 'monospace',
            fontSize: '0.8125rem',
            overflow: 'auto',
            maxHeight,
            borderRadius: BORDER_RADIUS.sm,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {str}
          </Box>
        </Paper>
      </Box>
    );
  };

  const handleResponseBodyExpand = useCallback(
    (entry: ProxyLogEntry) => {
      if (entry.responseBody == null) return;
      const sizeBytes = getResponseBodyByteSize(entry.responseBody);
      if (sizeBytes > MAX_RESPONSE_BODY_BYTES) {
        setResponseBodyOverflowDialog({ open: true, sizeBytes });
      } else {
        setResponseBodyFullViewEntryId(entry.id);
      }
    },
    []
  );

  const handleRequestBodyExpand = useCallback((entry: ProxyLogEntry) => {
    if (entry.requestBody == null) return;
    setRequestBodyFullViewEntryId(entry.id);
  }, []);

  const renderResponseBodySection = (entry: ProxyLogEntry) => {
    if (entry.responseBody == null) return null;
    const sizeBytes = getResponseBodyByteSize(entry.responseBody);
    const isOverflow = sizeBytes > MAX_RESPONSE_BODY_BYTES;
    const isTruncatedPreview = !isOverflow && sizeBytes > RESPONSE_BODY_PREVIEW_MAX_BYTES;
    const str = isOverflow
      ? ''
      : isTruncatedPreview
        ? truncateStringToBytes(bodyToString(entry.responseBody), RESPONSE_BODY_PREVIEW_MAX_BYTES)
        : bodyToString(entry.responseBody);

    return (
      <Box sx={{ mt: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 0.5,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}
          >
            Response body
          </Typography>
          <Tooltip
            title={
              isOverflow
                ? 'Response body exceeds 5 MB limit'
                : 'View Full Response Body'
            }
            describeChild
          >
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleResponseBodyExpand(entry);
              }}
              aria-label={
                isOverflow ? 'View size limit message' : 'View Full Response Body'
              }
              sx={{
                color: isOverflow ? 'warning.main' : 'primary.main',
                '&:hover': {
                  bgcolor: isOverflow
                    ? alpha(theme.palette.warning.main, 0.08)
                    : alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <OpenFullIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {isOverflow ? (
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              bgcolor: alpha(theme.palette.warning.main, 0.06),
              borderRadius: BORDER_RADIUS.sm,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Response body is too large to display ({formatBytesToMB(sizeBytes)} MB). Maximum size is 5 MB. Click the
              expand icon to see details.
            </Typography>
          </Paper>
        ) : (
          <>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                bgcolor: alpha(theme.palette.background.default, 0.6),
                fontFamily: 'monospace',
                fontSize: '0.8125rem',
                overflow: 'auto',
                maxHeight: 240,
                borderRadius: BORDER_RADIUS.sm,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {str}
              </Box>
            </Paper>
            {isTruncatedPreview && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                Showing first 10 KB of {formatByteSize(sizeBytes)}. Use View Full Response Body to inspect the
                complete payload.
              </Typography>
            )}
          </>
        )}
      </Box>
    );
  };

  const responseBodyFullViewEntry = responseBodyFullViewEntryId
    ? entries.find((e) => e.id === responseBodyFullViewEntryId)
    : null;
  const requestBodyFullViewEntry = requestBodyFullViewEntryId
    ? entries.find((e) => e.id === requestBodyFullViewEntryId)
    : null;

  useEffect(() => {
    if (!responseBodyFullViewEntry && !requestBodyFullViewEntry) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setResponseBodyFullViewEntryId(null);
        setRequestBodyFullViewEntryId(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [responseBodyFullViewEntry, requestBodyFullViewEntry]);

  const rootPaddingX = embedded ? SPACING.sm : isSmall ? SPACING.sm : SPACING.lg;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: embedded ? '100%' : 1200,
        mx: embedded ? 0 : 'auto',
        py: embedded ? SPACING.sm : SPACING.md,
        px: rootPaddingX,
        ...(embedded && { height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }),
        ...(standalone && { minHeight: '100vh', display: 'flex', flexDirection: 'column' }),
      }}
    >
      {/* Toolbar — Postman-style */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: SPACING.md,
          mb: SPACING.md,
          pb: SPACING.sm,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: SPACING.sm }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: BORDER_RADIUS.sm,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <TerminalIcon sx={{ fontSize: 20, color: 'primary.main' }} aria-hidden />
          </Box>
          <Box>
            <Typography variant="subtitle1" component="h1" fontWeight={700} color="text.primary">
              Console
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Proxy request logs
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={(_, v) => v != null && setFilter(v)}
            size="small"
            aria-label="Filter proxy logs"
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontWeight: 600,
                px: 1.5,
              },
            }}
          >
            <ToggleButton value="all" aria-pressed={filter === 'all'}>
              All
            </ToggleButton>
            <ToggleButton value="requests" aria-pressed={filter === 'requests'}>
              Requests
            </ToggleButton>
            <ToggleButton value="errors" aria-pressed={filter === 'errors'}>
              Errors
            </ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Clear all logs" describeChild>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClear}
              disabled={entries.length === 0}
              aria-label="Clear all proxy logs"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Clear
            </Button>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            onClick={handleLevelMenuOpen}
            aria-haspopup="true"
            aria-expanded={Boolean(levelMenuAnchorEl)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {allLevelsSelected ? 'All Logs' : someLevelsSelected ? 'Filtered Logs' : 'No Logs'}
          </Button>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showTimestamp}
                onChange={(e) => setShowTimestamp(e.target.checked)}
              />
            }
            label="Timestamp"
            sx={{ ml: 0 }}
          />
          {embedded && onClose ? (
            <Tooltip title="Close console">
              <IconButton
                size="small"
                onClick={onClose}
                aria-label="Close console"
                edge="end"
                sx={{ color: 'text.secondary' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      </Box>

      <Menu
        anchorEl={levelMenuAnchorEl}
        open={Boolean(levelMenuAnchorEl)}
        onClose={handleLevelMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {(['log', 'info', 'warning', 'error'] as LogLevel[]).map((level) => (
          <MenuItem key={level} onClick={() => toggleLevel(level)}>
            <Checkbox size="small" checked={levels[level]} tabIndex={-1} disableRipple />
            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
              {level}
            </Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* Request list card */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: BORDER_RADIUS.sm,
          overflow: 'hidden',
          boxShadow: ELEVATION.card,
          ...(embedded && { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }),
          ...(standalone && { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }),
        }}
      >
        {filteredEntries.length === 0 ? (
          <Box
            sx={{
              py: SPACING.xxl,
              px: SPACING.lg,
              textAlign: 'center',
              color: 'text.secondary',
              ...((embedded || standalone) && {
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }),
            }}
            role="status"
            aria-label="No proxy logs"
          >
            <TerminalIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
            <Typography variant="body1">
              {entries.length === 0
                ? 'Proxy requests will appear here when you send requests from Collections or Test Cycles.'
                : `No ${filter === 'errors' ? 'errors' : 'requests'} to show.`}
            </Typography>
          </Box>
        ) : (
          <List
            disablePadding
            sx={{
              overflow: 'auto',
              ...(embedded
                ? { flex: 1, minHeight: 0 }
                : standalone
                  ? { flex: 1, minHeight: 0 }
                  : { maxHeight: 'calc(100vh - 280px)' }),
            }}
          >
            {filteredEntries.map((entry) => {
              const isExpanded = expandedId === entry.id;
              const isError = entry.error != null;
              const methodColor = METHOD_COLORS[entry.method?.toUpperCase()] ?? theme.palette.text.secondary;

              return (
                <Box key={entry.id} component="li" sx={{ listStyle: 'none' }}>
                  <ListItemButton
                    onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                    sx={{
                      py: 1.25,
                      px: 2,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      alignItems: 'flex-start',
                      gap: 1.5,
                    }}
                    aria-expanded={isExpanded}
                    aria-controls={`console-details-${entry.id}`}
                    id={`console-row-${entry.id}`}
                  >
                    {showTimestamp && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontFamily: 'monospace',
                          minWidth: 72,
                          flexShrink: 0,
                          mt: 0.5,
                          fontSize: '0.7rem',
                        }}
                      >
                        {formatTime(entry.timestamp)}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, mt: 0.25 }}>
                      {isExpanded ? (
                        <ExpandLessIcon fontSize="small" color="action" aria-hidden />
                      ) : (
                        <ExpandMoreIcon fontSize="small" color="action" aria-hidden />
                      )}
                    </Box>
                    <Chip
                      label={entry.method ?? '?'}
                      size="small"
                      sx={{
                        minWidth: 52,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        bgcolor: alpha(methodColor, 0.12),
                        color: methodColor,
                        border: `1px solid ${alpha(methodColor, 0.35)}`,
                      }}
                      aria-hidden
                    />
                    {isError && (
                      <ErrorIcon fontSize="small" color="error" sx={{ flexShrink: 0 }} aria-label="Error" />
                    )}
                    {!isError && entry.status != null && entry.status >= 200 && entry.status < 300 && (
                      <SuccessIcon fontSize="small" color="success" sx={{ flexShrink: 0 }} aria-hidden />
                    )}
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{
                            fontFamily: 'monospace',
                            wordBreak: 'break-all',
                            fontWeight: 500,
                            fontSize: '0.8125rem',
                          }}
                        >
                          {formatUrlForConsoleDisplay(entry.url)}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5, alignItems: 'center' }}>
                          {entry.error ? (
                            <Typography variant="caption" color="error">
                              {entry.error}
                            </Typography>
                          ) : (
                            <>
                              {entry.status != null && (
                                <Chip
                                  label={entry.status}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                                />
                              )}
                              {entry.durationMs != null && (
                                <Typography variant="caption" color="text.secondary">
                                  {entry.durationMs} ms
                                </Typography>
                              )}
                            </>
                          )}
                        </Box>
                      }
                      primaryTypographyProps={{ component: 'span' }}
                    />
                  </ListItemButton>
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box
                      id={`console-details-${entry.id}`}
                      role="region"
                      aria-labelledby={`console-row-${entry.id}`}
                      sx={{
                        px: 2,
                        pb: 2,
                        pt: 0,
                        bgcolor: alpha(theme.palette.background.default, 0.5),
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {entry.requestHeaders && Object.keys(entry.requestHeaders).length > 0 && (
                        <Box sx={{ mt: 1.5 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}
                          >
                            Request headers
                          </Typography>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1,
                              fontFamily: 'monospace',
                              fontSize: '0.8125rem',
                              borderRadius: BORDER_RADIUS.sm,
                              border: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            {Object.entries(entry.requestHeaders).map(([k, v]) => (
                              <Box key={k} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Typography component="span" color="primary.main" fontWeight={600}>
                                  {k}:
                                </Typography>
                                <Typography component="span" variant="body2">
                                  {v}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopy(`${k}: ${v}`);
                                  }}
                                  aria-label={`Copy ${k} header`}
                                >
                                  <CopyIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ))}
                          </Paper>
                        </Box>
                      )}
                      {entry.responseHeaders && Object.keys(entry.responseHeaders).length > 0 && (
                        <Box sx={{ mt: 1.5 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}
                          >
                            Response headers
                          </Typography>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1,
                              fontFamily: 'monospace',
                              fontSize: '0.8125rem',
                              borderRadius: BORDER_RADIUS.sm,
                              border: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            {Object.entries(entry.responseHeaders).map(([k, v]) => {
                              const displayValue = formatResponseHeaderValue(k, v);
                              return (
                              <Box key={k} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Typography component="span" color="primary.main" fontWeight={600}>
                                  {k}:
                                </Typography>
                                <Typography component="span" variant="body2">
                                  {displayValue}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopy(`${k}: ${displayValue}`);
                                  }}
                                  aria-label={`Copy ${k} header`}
                                >
                                  <CopyIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              );
                            })}
                          </Paper>
                        </Box>
                      )}
                      {renderBodyBlock(entry.requestBody, 'Request body', {
                        onExpand: () => handleRequestBodyExpand(entry),
                        expandTitle: 'View Full Request Body',
                      })}
                      {renderResponseBodySection(entry)}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </List>
        )}
      </Paper>

      {/* Response body full view overlay — portaled above AppBar so Back is always reachable */}
      {responseBodyFullViewEntry &&
        createPortal(
          <Paper
            elevation={8}
            sx={{
              position: 'fixed',
              top: APP_HEADER_HEIGHT_PX,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: theme.zIndex.modal + 6,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 0,
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.5,
                borderBottom: `1px solid ${theme.palette.divider}`,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                flexShrink: 0,
              }}
            >
              <Button
                size="small"
                variant="outlined"
                startIcon={<BackIcon />}
                onClick={() => setResponseBodyFullViewEntryId(null)}
                aria-label="Back to console"
                sx={{ flexShrink: 0, mr: 0.5 }}
              >
                Back
              </Button>
              <Typography variant="subtitle1" fontWeight={600} sx={{ flexShrink: 0 }}>
                Response body
              </Typography>
              <Chip
                label={responseBodyFullViewEntry.method ?? '?'}
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  flexShrink: 0,
                  bgcolor: alpha(
                    METHOD_COLORS[responseBodyFullViewEntry.method?.toUpperCase()] ?? theme.palette.text.secondary,
                    0.15
                  ),
                  color: METHOD_COLORS[responseBodyFullViewEntry.method?.toUpperCase()] ?? theme.palette.text.secondary,
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                title={responseBodyFullViewEntry.url}
                sx={{
                  fontFamily: 'monospace',
                  ml: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatUrlForConsoleDisplay(responseBodyFullViewEntry.url)}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Tooltip title="Close">
                <IconButton
                  size="small"
                  onClick={() => setResponseBodyFullViewEntryId(null)}
                  aria-label="Close response body view"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 2,
                fontFamily: 'monospace',
                fontSize: '0.8125rem',
                bgcolor: alpha(theme.palette.background.default, 0.5),
              }}
            >
              <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {bodyToString(responseBodyFullViewEntry.responseBody)}
              </Box>
            </Box>
          </Paper>,
          document.body
        )}

      {/* Request body full view overlay — same maximize view as response body */}
      {requestBodyFullViewEntry &&
        createPortal(
          <Paper
            elevation={8}
            sx={{
              position: 'fixed',
              top: APP_HEADER_HEIGHT_PX,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: theme.zIndex.modal + 6,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 0,
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.5,
                borderBottom: `1px solid ${theme.palette.divider}`,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                flexShrink: 0,
              }}
            >
              <Button
                size="small"
                variant="outlined"
                startIcon={<BackIcon />}
                onClick={() => setRequestBodyFullViewEntryId(null)}
                aria-label="Back to console"
                sx={{ flexShrink: 0, mr: 0.5 }}
              >
                Back
              </Button>
              <Typography variant="subtitle1" fontWeight={600} sx={{ flexShrink: 0 }}>
                Request body
              </Typography>
              <Chip
                label={requestBodyFullViewEntry.method ?? '?'}
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  flexShrink: 0,
                  bgcolor: alpha(
                    METHOD_COLORS[requestBodyFullViewEntry.method?.toUpperCase()] ?? theme.palette.text.secondary,
                    0.15
                  ),
                  color: METHOD_COLORS[requestBodyFullViewEntry.method?.toUpperCase()] ?? theme.palette.text.secondary,
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                title={requestBodyFullViewEntry.url}
                sx={{
                  fontFamily: 'monospace',
                  ml: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatUrlForConsoleDisplay(requestBodyFullViewEntry.url)}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Tooltip title="Close">
                <IconButton
                  size="small"
                  onClick={() => setRequestBodyFullViewEntryId(null)}
                  aria-label="Close request body view"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 2,
                fontFamily: 'monospace',
                fontSize: '0.8125rem',
                bgcolor: alpha(theme.palette.background.default, 0.5),
              }}
            >
              <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {bodyToString(requestBodyFullViewEntry.requestBody)}
              </Box>
            </Box>
          </Paper>,
          document.body
        )}

      {/* Response body overflow dialog (when body exceeds 5 MB) */}
      <Dialog
        open={responseBodyOverflowDialog.open}
        onClose={() => setResponseBodyOverflowDialog((prev) => ({ ...prev, open: false }))}
        aria-labelledby="response-body-overflow-dialog-title"
        aria-describedby="response-body-overflow-dialog-description"
        PaperProps={{
          sx: { borderRadius: BORDER_RADIUS.sm, minWidth: 320 },
        }}
      >
        <DialogTitle id="response-body-overflow-dialog-title">
          Response body too large
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="response-body-overflow-dialog-description">
            The response body size ({formatBytesToMB(responseBodyOverflowDialog.sizeBytes)} MB) exceeds the 5 MB
            display limit. It cannot be shown in the console to avoid performance issues.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setResponseBodyOverflowDialog((prev) => ({ ...prev, open: false }))}
            variant="contained"
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {copySnack && (
        <Typography
          variant="caption"
          sx={{
            position: 'fixed',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: 'success.main',
            color: 'success.contrastText',
            px: 2,
            py: 1,
            borderRadius: 1,
            zIndex: theme.zIndex.snackbar,
          }}
          role="status"
        >
          Copied to clipboard
        </Typography>
      )}
    </Box>
  );
};

export default Console;
